<template>
  <b-row>
    <b-col sm="12">
      <b-card body-class="py-2">
        <section class="list-view-heading">
          <ModuleViewHeading
            :module-id="access.module_id"
            :profile-id="access.profile_id"
            :reference="access.view_reference"
            summary-only
          />
          <div class="list-view-heading__controls">
            <div class="list-view-heading__actions"><TabHeaderActions /></div>
          </div>
        </section>
      </b-card>

      <b-card>
        <div class="config-instances__section-title">
          <AppIcon name="key" class="mr-1" /> Integración con Monitor
        </div>
        <p class="text-muted mb-3">
          Monitor usa este token para consultar los servidores e instancias pendientes de emparejar, emparejarlos y enviar sus reportes.
          Hay un único token activo: al regenerarlo, el anterior deja de funcionar en el momento.
        </p>

        <div v-if="loading" class="py-3"><b-spinner small variant="primary" /> Cargando...</div>

        <div v-else>
          <div class="config-instances__status mb-3">
            <span v-if="status.configured" class="text-success">
              <AppIcon name="check_circle" /> Token configurado
            </span>
            <span v-else class="text-danger">
              <AppIcon name="cancel" /> Sin token: Monitor no puede acceder a la API
            </span>
          </div>

          <div v-if="newToken" class="config-instances__token mb-3">
            <b-alert show variant="warning" class="mb-2">
              <AppIcon name="warning" /> Copiá el token ahora: por seguridad no se vuelve a mostrar.
            </b-alert>
            <b-input-group size="sm">
              <b-form-input :value="newToken" readonly class="config-instances__token-input" />
              <b-input-group-append>
                <b-button variant="dark" @click="copy(newToken)"><AppIcon name="content_copy" class="mr-1" /> Copiar</b-button>
              </b-input-group-append>
            </b-input-group>
          </div>

          <b-button variant="dark" size="sm" class="mr-2" @click="generate()">
            <AppIcon name="autorenew" class="mr-1" /> {{status.configured ? 'Regenerar token' : 'Generar token'}}
          </b-button>
          <b-button v-if="status.configured" variant="outline-danger" size="sm" @click="revoke()">
            <AppIcon name="block" class="mr-1" /> Revocar token
          </b-button>
        </div>

        <hr>

        <div class="config-instances__section-title">
          <AppIcon name="api" class="mr-1" /> Datos de conexión para Monitor
        </div>
        <dl class="config-instances__info mb-0">
          <dt>URL base de la API</dt>
          <dd>
            <code>{{apiBaseURL}}</code>
            <b-button variant="link" size="sm" class="p-0 ml-2" @click="copy(apiBaseURL)" v-b-tooltip.hover title="Copiar URL"><AppIcon name="content_copy" /></b-button>
          </dd>
          <dt>Autenticación</dt>
          <dd><code>Authorization: Bearer &lt;token&gt;</code> o <code>X-Monitor-Token: &lt;token&gt;</code></dd>
          <dt>Documentación para Monitor</dt>
          <dd>Está debajo. Con "Descargar Markdown" se baja un archivo para pasarle al equipo de Monitor.</dd>
        </dl>
      </b-card>

      <b-card>
        <div v-if="loadingDocs" class="py-3"><b-spinner small variant="primary" /> Cargando documentación...</div>
        <MonitorApiDocs v-else-if="docs" :docs="docs" @copy="copy" @download-md="downloadMarkdown" />
      </b-card>

      <b-card body-class="py-2">
        <div class="list-view-footer">
          <div>
            <b-button type="button" variant="outline-dark" size="sm" @click="$router.go(-1)">
              <AppIcon name="arrow_back" class="mr-1" />
              Volver
            </b-button>
          </div>
        </div>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
  import serviceAPI from './services'
  import Error from '@/handler/error'
  import Profiles from '@/config/profiles'
  import Modules from '@/config/modules'
  import Helper from '@/handler/helper'
  import ConfigAPI from '@/config/api'
  import TabHeaderActions from '@/components/inc/tabs/TabHeaderActions'
  import MonitorApiDocs from './inc/monitorApiDocs'

  export default {
    components: {
      TabHeaderActions,
      MonitorApiDocs,
    },
    data: () => {
      return {
        access: {
          module_id: Modules.INSTANCIAS,
          profile_id: Profiles.PERSONAL,
          view_reference: 'configInstances',
          elements: {}
        },
        loading: false,
        status: {
          configured: false,
        },
        newToken: '',
        loadingDocs: false,
        docs: null,
        apiBaseURL: ConfigAPI.baseURL + 'instances/monitor/',
      }
    },
    created () {
      /* Configurar permisos de vistas y elementos */
      Helper.hasAccessView(this.access)
      /* Fin configuracion */
    },
    mounted() {
      this.load()
      this.loadDocs()
    },
    methods: {
      confirmDialog(message, options) {
        var danger = !!options.danger
        var icon = options.icon || (danger ? 'warning' : 'help')
        var title = this.$createElement('span', { class: 'app-confirm-header__title' }, [
          this.$createElement('AppIcon', { props: { name: icon } }),
          options.title
        ])
        return this.$bvModal.msgBoxConfirm(message, {
          title: title,
          size: 'sm',
          buttonSize: 'sm',
          okVariant: danger ? 'danger' : 'dark',
          okTitle: options.okTitle || 'Confirmar',
          cancelTitle: options.cancelTitle || 'Cancelar',
          footerClass: 'p-2',
          headerClass: danger ? 'app-confirm-header app-confirm-header--danger' : 'app-confirm-header',
          hideHeaderClose: false,
          centered: true
        })
      },
      load() {
        this.loading = true
        serviceAPI.obtenerMonitorToken()
          .then((response) => {
            this.status = response.data
            this.loading = false
          })
          .catch(error => {
            this.loading = false
            this.$awn.alert(Error.showError(error))
          })
      },
      loadDocs() {
        this.loadingDocs = true
        serviceAPI.obtenerMonitorDocs()
          .then((response) => {
            this.docs = response.data
            this.loadingDocs = false
          })
          .catch(error => {
            this.loadingDocs = false
            this.$awn.alert(Error.showError(error))
          })
      },
      downloadMarkdown() {
        let loader = this.$loading.show()
        serviceAPI.obtenerMonitorDocs('md')
          .then((response) => {
            loader.hide()
            var blob = new Blob([response.data.markdown], { type: 'text/markdown;charset=utf-8' })
            var link = document.createElement('a')
            link.href = URL.createObjectURL(blob)
            link.download = 'api-zyncro-monitor-v' + this.docs.version + '.md'
            document.body.appendChild(link)
            link.click()
            link.remove()
            URL.revokeObjectURL(link.href)
          })
          .catch(error => {
            loader.hide()
            this.$awn.alert(Error.showError(error))
          })
      },
      generate() {
        var message = this.status.configured
          ? '¿Desea regenerar el token? El token actual deja de funcionar y hay que actualizarlo en Monitor.'
          : '¿Desea generar el token de integración con Monitor?'

        this.confirmDialog(message, {
          title: this.status.configured ? 'Regenerar token' : 'Generar token',
          icon: 'key',
          danger: this.status.configured,
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (!value) return

          let loader = this.$loading.show()
          serviceAPI.generarMonitorToken()
            .then((response) => {
              loader.hide()
              this.newToken = response.data.token
              this.status = { configured: response.data.configured }
              this.$awn.success("Token generado")
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error))
            })
        })
      },
      revoke() {
        this.confirmDialog('¿Desea revocar el token? Monitor deja de tener acceso a la API hasta que se genere uno nuevo.', {
          title: 'Revocar token',
          icon: 'block',
          danger: true,
          okTitle: 'SI',
          cancelTitle: 'NO'
        })
        .then(value => {
          if (!value) return

          let loader = this.$loading.show()
          serviceAPI.revocarMonitorToken()
            .then((response) => {
              loader.hide()
              this.newToken = ''
              this.status = response.data
              this.$awn.success("Token revocado")
            })
            .catch(error => {
              loader.hide()
              this.$awn.alert(Error.showError(error))
            })
        })
      },
      copy(text) {
        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(text)
            .then(() => this.$awn.success('Copiado al portapapeles'))
            .catch(() => this.copyFallback(text))
          return
        }
        this.copyFallback(text)
      },
      copyFallback(text) {
        var textarea = document.createElement('textarea')
        textarea.value = text
        textarea.setAttribute('readonly', '')
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        var copied = document.execCommand('copy')
        textarea.remove()
        copied ? this.$awn.success('Copiado al portapapeles') : this.$awn.alert('No se pudo copiar')
      },
    }
  }
</script>

<style scoped>
  .config-instances__section-title {
    font-weight: 600;
    font-size: 15px;
    margin-bottom: 6px;
  }

  .config-instances__token-input {
    font-family: monospace;
  }

  .config-instances__info dt {
    font-weight: 600;
    margin-top: 6px;
  }

  .config-instances__info dd {
    margin-bottom: 4px;
    word-break: break-all;
  }
</style>
